import { useState, type ReactNode } from "react";
import { LogOut, Power, RotateCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMe } from "@/hooks/queries/use-me";
import { useGroomingBatches } from "@/hooks/queries/use-grooming";
import { useGroomingQueue } from "@/hooks/use-grooming-queue";
import type { GroomingBatchListEntry } from "@/lib/api/grooming";
import { useToolboxBridge, useToolboxEnvironment } from "@/lib/toolbox/bridge";
import { useClaudeChipRefresh } from "@/lib/toolbox/claude-signals";
import {
  signOutCounts,
  type EnvironmentReport,
  type SignOutConfirmCounts,
  type ToolboxBridge,
  type ToolboxSignOutResult,
} from "@ttc-spectree/toolbox-contract";
import { cn } from "@/lib/utils";
import { SignOutDialog } from "./sign-out-dialog";

/** Why the chip warns, if it does. */
export type ClaudeChipWarning = "usage-limit" | "signed-out" | null;

/**
 * Amber when the project's last batch stopped at the Claude usage limit or
 * the environment check reports Claude Code signed out. Pure; exported for tests.
 */
export function claudeChipWarning(
  lastBatch: Pick<GroomingBatchListEntry, "status" | "stopReason"> | undefined,
  environment: EnvironmentReport | undefined
): ClaudeChipWarning {
  if (environment?.shared["claude-signed-in"].status === "fail") return "signed-out";
  if (lastBatch?.status === "stopped" && lastBatch.stopReason === "usage-limit") return "usage-limit";
  return null;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "?";
}

/** The account menu's sign-out item (frame B5). */
export const SIGN_OUT_MENU_LABEL = "Sign out of SpecTree";
/** Windows only (gap G-02): the app menu's View > Reload, which Windows cannot show. */
export const RELOAD_MENU_LABEL = "Reload";
/** Windows only (gap G-02): the app menu's File > Quit, which Windows cannot show. */
export const QUIT_MENU_LABEL = "Quit";

/** Shown when the app could not sign out and gave no reason. */
const SIGN_OUT_FALLBACK_MESSAGE = "SpecTree Toolbox could not sign you out. Try again.";

/**
 * The app's safe sign-out (`app.signOut`, SpecTree feature PI-SPEC-SW-1485),
 * or null when the app does not offer it: an older app keeps only its own
 * menu's Sign Out. Pure; exported for tests.
 */
export function appSignOutOf(bridge: ToolboxBridge | null): (() => Promise<ToolboxSignOutResult>) | null {
  if (!bridge) return null;
  const app = bridge.app;
  return typeof app.signOut === "function" ? () => app.signOut?.() ?? Promise.resolve({ ok: false, message: "" }) : null;
}

/** The app's Reload and Quit for the account menu, each null when not offered. */
export interface AppWindowActions {
  reload: (() => Promise<void>) | null;
  quit: (() => Promise<void>) | null;
}

/**
 * Reload and Quit for the account menu (SpecTree Windows walk 1, gap G-02):
 * offered only on Windows (`app.titleBar.style` "windows-overlay"), where the
 * frameless window shows no menu bar, and only when the app has `app.reload`
 * / `app.quit` (an older app has neither, and the items stay hidden). On
 * macOS the system menu bar keeps them. Pure; exported for tests.
 */
export function appWindowActionsOf(bridge: ToolboxBridge | null): AppWindowActions {
  const none: AppWindowActions = { reload: null, quit: null };
  if (!bridge || bridge.app.titleBar?.style !== "windows-overlay") return none;
  const app = bridge.app;
  return {
    reload: typeof app.reload === "function" ? () => app.reload?.() ?? Promise.resolve() : null,
    quit: typeof app.quit === "function" ? () => app.quit?.() ?? Promise.resolve() : null,
  };
}

/**
 * The title bar's Claude account chip (design `.acct`): "Claude · <name> ·
 * Team" with a health dot, green when fine, amber with a glow when Claude
 * cannot run (usage limit reached, or signed out).
 *
 * Inside an app that offers `app.signOut` (SpecTree feature PI-SPEC-SW-1485,
 * frame B5) the chip opens the account menu with "Sign out of SpecTree".
 * With nothing running it signs out at once; while Claude is working it asks
 * first in the in-window dialog ("Keep working" is the default), and only
 * "Sign out and stop N briefs" signs out. On Windows the menu also offers
 * Reload and Quit (gap G-02: no menu bar there). With none of these the chip
 * is not interactive and the app menu's Sign Out stays the only one.
 *
 * The chip follows Claude's sign-in as Help and the runs see it (gap G-04,
 * `useClaudeChipRefresh`): a signed-out answer re-runs the environment checks.
 */
export function ClaudeChip({ project }: { project: string | undefined }) {
  const { data: me } = useMe();
  const batches = useGroomingBatches(project);
  const environment = useToolboxEnvironment();
  const bridge = useToolboxBridge();
  const signOut = appSignOutOf(bridge);
  const windowActions = appWindowActionsOf(bridge);
  useClaudeChipRefresh(bridge, environment.data);
  const lastRun = batches.data?.find((b) => b.status !== "draft");
  const warning = claudeChipWarning(lastRun, environment.data);
  const name = me?.name ?? "";
  const firstName = name.trim().split(/\s+/)[0] ?? "";

  const label =
    warning === "usage-limit"
      ? "Claude · usage limit reached"
      : warning === "signed-out"
        ? "Claude · signed out"
        : `Claude · ${firstName || "you"} · Team`;

  const content = (
    <>
      <span
        className="flex h-5 w-5 items-center justify-center rounded-full bg-[#27272A] text-[9.5px] font-semibold text-[#FAFAFA]"
        aria-hidden="true"
      >
        {initials(name)}
      </span>
      <span
        className={cn(
          "h-[7px] w-[7px] rounded-full",
          warning ? "animate-pulse bg-[#F59E0B] shadow-[0_0_0_3px_rgba(245,158,11,.25)]" : "bg-[#22C55E]"
        )}
        aria-hidden="true"
      />
      {label}
    </>
  );
  const chipClass =
    "flex flex-none items-center gap-[7px] whitespace-nowrap rounded-full border border-[#1F1F23] py-[3px] pl-1 pr-2.5 text-xs text-[#A1A1AA] [-webkit-app-region:no-drag]";

  if (signOut === null && windowActions.reload === null && windowActions.quit === null) {
    return (
      <span className={chipClass} data-testid="claude-chip" data-warning={warning ?? "none"}>
        {content}
      </span>
    );
  }
  return (
    <AccountMenu signOut={signOut} windowActions={windowActions} chipClass={chipClass} warning={warning}>
      {content}
    </AccountMenu>
  );
}

interface AccountMenuProps {
  signOut: (() => Promise<ToolboxSignOutResult>) | null;
  windowActions: AppWindowActions;
  chipClass: string;
  warning: ClaudeChipWarning;
  children: ReactNode;
}

const menuItemClass =
  "gap-[9px] rounded-md px-[9px] py-[7px] text-[13px] text-[#D4D4D8] hover:bg-[#27272A] hover:text-[#FAFAFA] focus:bg-[#27272A]";

/** The chip as the account menu's trigger, and the sign-out question (frame B5). */
function AccountMenu({ signOut, windowActions, chipClass, warning, children }: AccountMenuProps) {
  const { supported, state } = useGroomingQueue();
  const [asking, setAsking] = useState<SignOutConfirmCounts | "unknown" | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { reload, quit } = windowActions;

  const run = async () => {
    if (!signOut) return;
    setBusy(true);
    setError(null);
    try {
      const result = await signOut();
      // On success the app loads SpecTree's sign-in; this page goes away.
      if (!result.ok) setError(result.message || SIGN_OUT_FALLBACK_MESSAGE);
    } catch {
      setError(SIGN_OUT_FALLBACK_MESSAGE);
    } finally {
      setBusy(false);
    }
  };

  const choose = () => {
    // Sign out at once only when nothing can stop: an app without a queue, or a queue read with nothing running.
    // Before the queue is read (or when the read failed), ask without numbers.
    const counts = state ? signOutCounts(state) : null;
    if (!supported || counts?.running === 0) {
      void run();
      return;
    }
    setError(null);
    setAsking(counts ?? "unknown");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          type="button"
          aria-label="Account"
          className={cn(chipClass, "transition-colors hover:border-[#3F3F46] hover:bg-[#18181B] aria-expanded:border-[#3F3F46] aria-expanded:bg-[#18181B]")}
          data-testid="claude-chip"
          data-warning={warning ?? "none"}
        >
          {children}
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-[250px] rounded-[10px] border border-[#3F3F46] bg-[#18181B] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,.55)]"
        >
          {reload && (
            <DropdownMenuItem
              className={menuItemClass}
              onClick={() => {
                void reload().catch(() => undefined);
              }}
              data-testid="account-menu-reload"
            >
              <RotateCw className="h-4 w-4 flex-none" aria-hidden="true" />
              {RELOAD_MENU_LABEL}
            </DropdownMenuItem>
          )}
          {signOut && (
            <DropdownMenuItem className={menuItemClass} onClick={choose} data-testid="account-menu-sign-out">
              <LogOut className="h-4 w-4 flex-none" aria-hidden="true" />
              {SIGN_OUT_MENU_LABEL}
            </DropdownMenuItem>
          )}
          {quit && (
            <>
              {(reload !== null || signOut !== null) && <DropdownMenuSeparator className="my-1 bg-[#27272A]" />}
              <DropdownMenuItem
                className={menuItemClass}
                onClick={() => {
                  void quit().catch(() => undefined);
                }}
                data-testid="account-menu-quit"
              >
                <Power className="h-4 w-4 flex-none" aria-hidden="true" />
                {QUIT_MENU_LABEL}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {error && asking === null && (
        <button
          type="button"
          className="fixed right-3 top-[48px] z-50 max-w-[320px] rounded-lg border border-[rgba(225,24,55,.35)] bg-[#150C0E] px-3 py-2 text-left text-[13px] leading-[1.45] text-[#FF8A9B] shadow-[0_16px_40px_rgba(0,0,0,.55)] [-webkit-app-region:no-drag]"
          role="alert"
          onClick={() => {
            setError(null);
          }}
          data-testid="account-sign-out-error"
        >
          {error}
        </button>
      )}
      <SignOutDialog
        open={asking !== null}
        counts={asking ?? "unknown"}
        busy={busy}
        error={error}
        onKeepWorking={() => {
          setAsking(null);
          setError(null);
        }}
        onConfirm={() => {
          void run();
        }}
      />
    </>
  );
}
